import { useLazyGetProductFacetsQuery, useLazyProductSearchQuery } from '@api/product.api';
import { CommercePage, ProductCategory, ProductSorting } from '@api/types';
import { BlockContainer, CategoryView } from '@components';
import { useAppDispatch, useAppSelector } from '@hooks';
import { getCurrentPageDisplayName } from '@store/app/app.slice';
import {
  setCategoryBreadcrumbs,
  setCategoryFiltersWorking,
  setCategoryInitialWorking,
  setCategoryLink,
  setCategoryWorking,
} from '@store/catalog/catalog.slice';
import { getPageContentType } from '@utils/content.utils';
import { isActivePath } from '@utils/links.utils';
import { extractFacetKeysFromParams } from '@utils/products.utils';
import { useCallback, useEffect, useMemo, useState } from 'react';

// TYPES
//-------

export type CategoryBlockProps = {
  page?: CommercePage;
};

// STATE
//-------

export const INITIAL_CATEGORY_STATE = {
  offset: 0,
  limit: 12,
  sorting: 'PriceAsc' as ProductSorting,
  filters: [],
  itemPerPage: 12,
};

// COMPONENTS
//------------

/**
 * Display products and categories provided from wrapper component
 */
export const CategoryBlock = ({ page = {} }: CategoryBlockProps) => {
  const { contentLink = {} } = page || {};
  const { id, providerName } = contentLink;
  const title = useAppSelector(getCurrentPageDisplayName);
  const pageType = getPageContentType(page);
  const isWorking = useAppSelector((state) => state.catalog.isWorking);
  const currentCategoryLink = useMemo(
    () => (pageType === 'Category' ? `${id}__${providerName}` : ''),
    [id, pageType, providerName]
  );

  const products = useAppSelector((state) => state.products);
  const productItems = products.items;
  const totalCount = products.totalCount;
  const categories = products.categories;
  const facetsData = useAppSelector((state) => state.catalog.filters);

  const isCategorySelected = (category: ProductCategory) => isActivePath(category.url);

  const dispatch = useAppDispatch();

  const [triggerGetProducts] = useLazyProductSearchQuery();
  const [triggerGetProductFacets] = useLazyGetProductFacetsQuery();
  const [displayName, setDisplayName] = useState(title);

  const isCategoryOpened = (category: ProductCategory) => {
    if (isActivePath(category.url)) return true;
    return Boolean(category.nestedCategories?.find((subCat) => isActivePath(subCat.url)));
  };

  const handleCategoryChange = useCallback(
    (category: ProductCategory) => {
      if (category.url) {
        const history = window.history;
        history.pushState({}, '', category.url);

        dispatch(setCategoryWorking(true));
        dispatch(setCategoryFiltersWorking(true));
        const categoryName = category.displayName;
        const breadcrumbs = getCategoryBreadcrumbs(category, categories);
        dispatch(setCategoryBreadcrumbs(breadcrumbs));
        setDisplayName(categoryName);
        if (category.categoryLink) {
          dispatch(setCategoryLink(category.categoryLink || ''));
          triggerGetProductFacets({
            categoryLink: category.categoryLink || '',
          }).then(() => {
            dispatch(setCategoryFiltersWorking(false));

            triggerGetProducts({
              offset: INITIAL_CATEGORY_STATE.offset,
              limit: INITIAL_CATEGORY_STATE.itemPerPage,
              sorting: INITIAL_CATEGORY_STATE.sorting,
              categoryLink: category.categoryLink || '',
              filters: extractFacetKeysFromParams(facetsData),
            }).then(() => {
              dispatch(setCategoryWorking(false));
            });
          });
        }
      }
      if (category?.displayName) document.title = category.displayName;
    },
    [dispatch, triggerGetProductFacets, triggerGetProducts, facetsData, categories]
  );

  useEffect(() => {
    if (currentCategoryLink) {
      dispatch(setCategoryFiltersWorking(true));
      dispatch(setCategoryWorking(true));
      dispatch(setCategoryInitialWorking(true));
      dispatch(setCategoryLink(currentCategoryLink));
      triggerGetProductFacets({
        categoryLink: currentCategoryLink,
      }).then((res) => {
        dispatch(setCategoryFiltersWorking(false));
        if (res.data?.data && res?.isSuccess) {
          const searchParams = new URLSearchParams(window.location.search);
          const currentPage = Number(searchParams.get('page')) || 1;
          const currentParamsSorting =
            (searchParams.get('sorting') as ProductSorting) || INITIAL_CATEGORY_STATE.sorting;
          triggerGetProducts({
            offset: (currentPage - 1) * INITIAL_CATEGORY_STATE.itemPerPage,
            limit: INITIAL_CATEGORY_STATE.itemPerPage,
            sorting: currentParamsSorting,
            categoryLink: currentCategoryLink,
            filters: extractFacetKeysFromParams(res.data.data),
          }).then((res) => {
            const response = res.data?.data;
            const currentCattegory = findCategory(response?.categories, currentCategoryLink);
            const breadcrumbs = getCategoryBreadcrumbs(currentCattegory, response?.categories);
            dispatch(setCategoryBreadcrumbs(breadcrumbs));
            dispatch(setCategoryInitialWorking(false));
            dispatch(setCategoryWorking(false));
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BlockContainer noAddedPadding>
      <CategoryView
        title={displayName}
        showFiltersAndPagination={true}
        productItems={productItems}
        totalCount={totalCount}
        categories={categories}
        isWorking={isWorking}
        isCategorySelected={isCategorySelected}
        handleCategoryChange={handleCategoryChange}
        isCategoryOpened={isCategoryOpened}
      />
    </BlockContainer>
  );
};

const getCategoryBreadcrumbs = (
  category?: ProductCategory | null,
  categories?: ProductCategory[]
) => {
  let breadcrumbs: Array<{ displayName?: string; url?: string }> = [];

  if (category && categories) {
    const topLevelCategory = categories.find((cat) => cat.categoryLink === category.categoryLink);

    if (topLevelCategory) {
      breadcrumbs.push({
        displayName: topLevelCategory.displayName,
        url: topLevelCategory.url,
      });
    } else {
      for (const parentCategory of categories) {
        const nestedCategory = parentCategory.nestedCategories?.find(
          (nestedCat) => nestedCat.categoryLink === category.categoryLink
        );

        if (nestedCategory) {
          breadcrumbs.push({
            displayName: parentCategory.displayName,
            url: parentCategory.url,
          });
          breadcrumbs.push({
            displayName: nestedCategory.displayName,
            url: nestedCategory.url,
          });
          break;
        }
      }
    }
  }

  return breadcrumbs;
};

const findCategory = (
  categories?: ProductCategory[],
  targetCategoryLink?: string
): ProductCategory | null => {
  if (!categories) {
    return null;
  }
  for (const category of categories) {
    if (category.categoryLink === targetCategoryLink) {
      return category;
    }
    if (category.nestedCategories && category.nestedCategories.length > 0) {
      const result = findCategory(category.nestedCategories, targetCategoryLink);
      if (result) {
        return result;
      }
    }
  }
  return null;
};
